import React, { useMemo } from 'react';
import { sumBy, keyBy, mapValues, groupBy, chunk, } from 'lodash';
import { format as formatDate, getDaysInMonth, addDays } from 'date-fns';
import { useCounter, } from 'react-use';
import { collection, query, where } from 'firebase/firestore';

import { excludeTax } from '../../shared/util';
import {
  createPlItems,
  computePeriodValues,
  filterByDateRange,
  computeExpensesForMonth as computeExpensesForMonthShared,
} from '../../shared/plCalculation';
import { resolveAll as resolveRecurringExpenses } from '../../shared/models/recurringExpense';
import useTenant from './useTenant';
import useCollectionsFetch from './useCollectionsFetch';
import useCollectionSubscription from './useCollectionSubscription';

export default function usePlCalculation({ dateRange, dateRanges, sellers, shops = null, dateRangeType = 'daily', isExcludeTax = false }) {
  const sellerIds = sellers.map(_ => _.id);
  const shopIds = (shops || []).map(_ => _.id);
  const [version, { inc: reload, }] = useCounter();

  const periods = useMemo(() => dateRanges || [dateRange], [dateRange, dateRanges]);
  const isMultiplePeriods = Boolean(dateRanges);

  const overallRange = useMemo(() => {
    if (!periods.length) return [new Date(), new Date()];
    const starts = periods.map(_ => _[0]);
    const ends = periods.map(_ => _[1]);
    return [new Date(Math.min(...starts)), new Date(Math.max(...ends))];
  }, [periods]);

  const month = formatDate(overallRange[0], 'yyyy-MM');
  const monthDays = getDaysInMonth(overallRange[0]);

  const { tenant, } = useTenant();

  const productChunks = useCollectionSubscription(query(collection(tenant.ref, 'productChunks')));
  const products = useMemo(_ => productChunks.flatMap(_ => Object.entries(_.data || {}).map(([id, product]) => ({ ...product, id }))), [productChunks]);

  const fbaStorageFees = useCollectionsFetch(dateRangeType === 'monthly' && sellers.length > 0 && periods.map(([start]) =>
    query(collection(tenant.ref, 'fbaStorageFees'),
      where('sellerId', 'in', sellers.map(_ => _.id)),
      where('month', '==', formatDate(start, 'yyyy-MM'))
    )
  ), [tenant, sellers, periods]);

  const dateRangeArgs = [tenant, ...overallRange.map(_ => formatDate(_, 'yyyy-MM-dd'))];
  const _amazonProductsSales = useCollectionSubscription(query(collection(tenant.ref, 'amazonProductsSales'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);
  const _amazonDailySales = useCollectionSubscription(query(collection(tenant.ref, 'amazonDailySales'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);
  const _amazonAdCosts = useCollectionSubscription(query(collection(tenant.ref, 'amazonAdCosts'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);
  const _rakutenProductsSales = useCollectionSubscription(query(collection(tenant.ref, 'rakutenProductsSales'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);
  const _rakutenDailySales = useCollectionSubscription(query(collection(tenant.ref, 'rakutenDailySales'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);
  const _rakutenProductsAdKpis = useCollectionSubscription(query(collection(tenant.ref, 'rakutenProductsAdKpis'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);
  const _shopifyProductsSales = useCollectionSubscription(query(collection(tenant.ref, 'shopifyProductsSales'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);
  const _shopifyDailySales = useCollectionSubscription(query(collection(tenant.ref, 'shopifyDailySales'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);
  const _yahooProductsSales = useCollectionSubscription(query(collection(tenant.ref, 'yahooProductsSales'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);
  const _yahooDailySales = useCollectionSubscription(query(collection(tenant.ref, 'yahooDailySales'), where('date', '>=', formatDate(overallRange[0], 'yyyy-MM-dd')), where('date', '<=', formatDate(overallRange[1], 'yyyy-MM-dd'))), dateRangeArgs);

  const allAmazonProductsSales = _amazonProductsSales.filter(_ => sellerIds.includes(_.sellerId));
  const allAmazonDailySales = _amazonDailySales.filter(_ => sellerIds.includes(_.sellerId));
  const allAmazonAdCosts = _amazonAdCosts.filter(_ => sellerIds.includes(_.sellerId));
  const isFiltered = shops !== null;
  const allRakutenProductsSales = isFiltered ? _rakutenProductsSales.filter(_ => shopIds.includes(_.shopId)) : _rakutenProductsSales;
  const allRakutenDailySales = isFiltered ? _rakutenDailySales.filter(_ => shopIds.includes(_.shopId)) : _rakutenDailySales;
  const allRakutenAdCosts = isFiltered ? _rakutenProductsAdKpis.filter(_ => shopIds.includes(_.shopId)) : _rakutenProductsAdKpis;
  const allShopifyProductsSales = isFiltered ? _shopifyProductsSales.filter(_ => shopIds.includes(_.shopId)) : _shopifyProductsSales;
  const allShopifyDailySales = isFiltered ? _shopifyDailySales.filter(_ => shopIds.includes(_.shopId)) : _shopifyDailySales;
  const allYahooProductsSales = isFiltered ? _yahooProductsSales.filter(_ => shopIds.includes(_.shopId)) : _yahooProductsSales;
  const allYahooDailySales = isFiltered ? _yahooDailySales.filter(_ => shopIds.includes(_.shopId)) : _yahooDailySales;

  const handleTax = isExcludeTax ? excludeTax : _ => _;

  // 売上 values のキーは Amazon=sku、楽天=productId(docID)。
  // Amazonと楽天で同じSKUを使うとsku単独キーが衝突するため、Amazon商品(sellerId持ち)のみskuキー、楽天はidキーで登録する。
  const amazonProducts = useMemo(_ => products.filter(_ => _.sellerId), [products]);
  // 原価は売上docの日付ごとに createPlItems 内で解決するため、数値化せず product オブジェクトのまま渡す（unitCostHistory を含む）
  const productsBySku = useMemo(_ => ({
    ...keyBy(amazonProducts, 'sku'),
    ...keyBy(products, 'id'),
  }), [products, amazonProducts]);

  const unitShippingCostsBySku = useMemo(_ => ({
    ...mapValues(keyBy(amazonProducts, 'sku'), product => {
      const cost = product.unitShippingCost || 0;
      return isExcludeTax ? excludeTax(cost) : cost;
    }),
    ...mapValues(keyBy(products, 'id'), product => {
      const cost = product.unitShippingCost || 0;
      return isExcludeTax ? excludeTax(cost) : cost;
    }),
  }), [products, amazonProducts, isExcludeTax]);

  const ownerIds = [...sellerIds, ...shopIds];
  const expenses = useCollectionsFetch(dateRangeType === 'monthly' && ownerIds.length > 0 && periods.flatMap(([start]) => [
    ...chunk(ownerIds, 10).map(idsChunk =>
      query(collection(tenant.ref, 'expenses'),
        where('ownerId', 'in', idsChunk),
        where('month', '==', formatDate(start, 'yyyy-MM'))
      )
    ),
    // 未移行データ（旧sellerIdフィールド）のフォールバック。マイグレーション後は削除
    ...chunk(sellerIds, 10).map(idsChunk =>
      query(collection(tenant.ref, 'expenses'),
        where('sellerId', 'in', idsChunk),
        where('month', '==', formatDate(start, 'yyyy-MM'))
      )
    ),
  ]), [tenant, sellers, shops, periods]);
  const recurringExpenses = useCollectionSubscription(dateRangeType === 'monthly' && query(collection(tenant.ref, 'recurringExpenses')), [tenant, dateRangeType]);
  const allExpenses = useMemo(() => [
    ...expenses,
    ...(dateRangeType === 'monthly' ? periods.flatMap(([start]) => resolveRecurringExpenses(recurringExpenses, formatDate(start, 'yyyy-MM'))).filter(_ => ownerIds.includes(_.ownerId)) : []),
  ], [expenses, recurringExpenses, periods, dateRangeType, ownerIds.join(',')]);
  const computedExpenses = mapValues(
    groupBy(allExpenses, 'behavior'),
    _ => mapValues(
      groupBy(_, 'type'),
      _ => handleTax(sumBy(_, 'amount')),
    ),
  );

  const calculatePeriodData = (periodRange) => {
    const [start, end] = periodRange;
    const startStr = formatDate(start, 'yyyy-MM-dd');
    const endStr = formatDate(end, 'yyyy-MM-dd');

    const amazonProductsSales = filterByDateRange(allAmazonProductsSales, startStr, endStr);
    const amazonDailySales = filterByDateRange(allAmazonDailySales, startStr, endStr);
    const amazonAdCosts = filterByDateRange(allAmazonAdCosts, startStr, endStr);
    const rakutenAdCosts = filterByDateRange(allRakutenAdCosts, startStr, endStr);
    const rakutenProductsSales = filterByDateRange(allRakutenProductsSales, startStr, endStr);
    const rakutenDailySales = filterByDateRange(allRakutenDailySales, startStr, endStr);
    const shopifyProductsSales = filterByDateRange(allShopifyProductsSales, startStr, endStr);
    const shopifyDailySales = filterByDateRange(allShopifyDailySales, startStr, endStr);
    const yahooProductsSales = filterByDateRange(allYahooProductsSales, startStr, endStr);
    const yahooDailySales = filterByDateRange(allYahooDailySales, startStr, endStr);

    const allDailySales = [...amazonDailySales, ...rakutenDailySales, ...shopifyDailySales, ...yahooDailySales];
    const allProductsSales = [...amazonProductsSales, ...rakutenProductsSales, ...shopifyProductsSales, ...yahooProductsSales];
    const values = computePeriodValues({ allDailySales }, handleTax);

    return { allProductsSales, allDailySales, amazonAdCosts, rakutenAdCosts, values };
  };

  const currentPeriodData = calculatePeriodData(periods[0] || [new Date(), new Date()]);
  const { allProductsSales, allDailySales, amazonAdCosts, rakutenAdCosts, values } = currentPeriodData;

  const periodData = useMemo(() => {
    if (!isMultiplePeriods) return null;

    return periods.map((period, index) => {
      const data = calculatePeriodData(period);
      const salesAmount = data.values?.sales || 0;
      const [startDate, endDate] = period;
      const periodMonth = formatDate(startDate, 'yyyy-MM');
      const periodDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      const periodComputedExpenses = computeExpensesForMonthShared(allExpenses, periodMonth, handleTax);
      const periodFbaStorageFees = fbaStorageFees.filter(_ => _.month === periodMonth);

      const items = createPlItems({...data, productsBySku, unitShippingCostsBySku, fbaStorageFees: periodFbaStorageFees}, {
        sellers,
        shops,
        dateRangeType,
        computedExpenses: periodComputedExpenses,
        periodStart: startDate,
        monthDays: periodDays,
        handleTax,
        isExcludeTax,
      });

      return {
        period,
        startDate,
        endDate,
        salesAmount,
        items,
        data,
      };
    });
  }, [periods, isMultiplePeriods, allAmazonDailySales, allAmazonProductsSales, allAmazonAdCosts, allRakutenDailySales, allRakutenProductsSales, allRakutenAdCosts,
    allShopifyDailySales, allShopifyProductsSales, allYahooDailySales, allYahooProductsSales, productsBySku, fbaStorageFees, dateRangeType, allExpenses]);

  const items = useMemo(() => createPlItems({...currentPeriodData, productsBySku, unitShippingCostsBySku, fbaStorageFees}, {
    sellers,
    shops,
    dateRangeType,
    computedExpenses,
    periodStart: periods[0]?.[0],
    monthDays,
    handleTax,
    isExcludeTax,
  }), [currentPeriodData, fbaStorageFees, sellers, dateRangeType, computedExpenses, monthDays, isExcludeTax, productsBySku]);

  const salesAmount = items.sales.computeValue();

  const dailyData = useMemo(() => {
    if (dateRangeType !== 'weekly') return null;

    const weekDates = Array.from({ length: 7 }, (_, i) => addDays(dateRange[0], i));

    return weekDates.map(date => {
      const dateStr = formatDate(date, 'yyyy-MM-dd');
      const dailySalesForDate = allDailySales.filter(_ => _.date === dateStr);
      const productSalesForDate = allProductsSales.filter(_ => _.date === dateStr);
      const adCostsForDate = amazonAdCosts.filter(_ => _.date === dateStr);
      const rakutenAdCostsForDate = rakutenAdCosts.filter(_ => _.date === dateStr);

      const dayValues = computePeriodValues({ allDailySales: dailySalesForDate }, handleTax);

      const dayData = {
        allProductsSales: productSalesForDate,
        values: dayValues,
        productsBySku,
        unitShippingCostsBySku,
        fbaStorageFees,
        amazonAdCosts: adCostsForDate,
        rakutenAdCosts: rakutenAdCostsForDate
      };

      const dayItems = createPlItems(dayData, {
        sellers,
        shops,
        dateRangeType: 'daily',
        computedExpenses: {},
        monthDays,
        handleTax,
        isExcludeTax,
      });

      return {
        date,
        dateStr,
        items: dayItems,
        salesAmount: dayItems.sales.computeValue(),
      };
    });
  }, [dateRangeType, dateRange, allDailySales, allProductsSales, amazonAdCosts, rakutenAdCosts, productsBySku, fbaStorageFees, monthDays, isExcludeTax]);

  return {
    items,
    salesAmount,
    dailyData,
    periodData,
    reload,
  };
};
